import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';

/** "0:07" for a video moment. */
export const fmtTime = (t) => {
  const s = Math.max(0, Math.floor(t || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Comments that point at a spot, numbered in thread order: [{ n, index, x, y, t }]. */
export const pinsFromComments = (comments = []) => {
  let n = 0;
  return comments.flatMap((c, index) => (c.pin ? [{ n: (n += 1), index, ...c.pin }] : []));
};

const NEAR_SECONDS = 1.5;

function Pin({ n, x, y, color, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'absolute', left: `${x * 100}%`, top: `${y * 100}%`, transform: 'translate(-50%, -50%)',
        width: 24, height: 24, borderRadius: '50%', bgcolor: color, color: '#fff', border: '2px solid #fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, cursor: onClick ? 'pointer' : 'default', pointerEvents: 'auto', zIndex: 2,
      }}
    >
      {n}
    </Box>
  );
}

Pin.propTypes = {
  n: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

/**
 * An ad image or video with comment pins on it, Frame.io-style. Pins sit at
 * fractions of the frame, so they land on the same spot at any size. On a
 * video a pin shows around its moment. With ``placing`` on, a click drops a
 * new pin (pausing the video) and reports ``{x, y, t}`` to ``onPlace``.
 */
export default function PinnableMedia({
  asset, pins = [], draft = null, placing = false, onPlace = null, onPinClick = null, seek = null, height = 360,
}) {
  const videoRef = useRef(null);
  const [now, setNow] = useState(0);
  const isVideo = asset.asset_type === 'video';

  useEffect(() => {
    if (!seek || !videoRef.current) return;
    videoRef.current.currentTime = seek.t || 0;
    videoRef.current.pause();
    setNow(seek.t || 0);
  }, [seek]);

  const place = (e) => {
    if (!placing || !onPlace) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    const y = Math.min(1, Math.max(0, (e.clientY - box.top) / box.height));
    const pin = { x, y };
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
      pin.t = videoRef.current.currentTime;
    }
    onPlace(pin);
  };

  const shown = (p) => !isVideo || p.t == null || Math.abs(now - p.t) <= NEAR_SECONDS;

  return (
    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 0, maxWidth: '100%' }}>
        {isVideo ? (
          <video
            key={asset.id} ref={videoRef} src={asset.file_url} controls={!placing}
            onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
            onSeeked={(e) => setNow(e.currentTarget.currentTime)}
            style={{ maxWidth: '100%', maxHeight: height, display: 'block' }}
          />
        ) : (
          <img key={asset.id} src={asset.file_url} alt={asset.label || ''}
            style={{ maxWidth: '100%', maxHeight: height, display: 'block', objectFit: 'contain' }} />
        )}
        <Box
          onClick={place}
          sx={{
            position: 'absolute', inset: 0, pointerEvents: placing ? 'auto' : 'none',
            cursor: placing ? 'crosshair' : 'default',
            outline: placing ? '2px dashed #ff680d' : 'none', outlineOffset: -2,
          }}
        >
          {pins.filter(shown).map((p) => (
            <Pin key={p.n} n={p.n} x={p.x} y={p.y} color="#ff680d"
              onClick={onPinClick ? (e) => { e.stopPropagation(); onPinClick(p); } : undefined} />
          ))}
          {draft && <Pin n="+" x={draft.x} y={draft.y} color="#2563eb" />}
        </Box>
        {placing && (
          <Chip size="small" label={isVideo ? 'Click the spot — the video pauses there' : 'Click the spot'}
            sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', bgcolor: '#111827', color: '#fff', zIndex: 3 }} />
        )}
      </Box>
      {!asset.file_url && <Typography variant="body2" color="text.secondary">No file</Typography>}
    </Box>
  );
}

PinnableMedia.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.number,
    asset_type: PropTypes.string,
    file_url: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  pins: PropTypes.arrayOf(PropTypes.shape({ n: PropTypes.number, x: PropTypes.number, y: PropTypes.number, t: PropTypes.number })),
  draft: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number, t: PropTypes.number }),
  placing: PropTypes.bool,
  onPlace: PropTypes.func,
  onPinClick: PropTypes.func,
  seek: PropTypes.shape({ t: PropTypes.number }),
  height: PropTypes.number,
};

